import { ComponentType } from "react";
import { createRoot } from "react-dom/client";
import { Config } from "@kaltura/mediaspace-shared-data-kms-config/Config";
import { TopLevelComponentWrappers } from "./TopLevelComponentWrappers";

/**
 * Render standalone component in DS context.
 * This function could be used directly on the PHP side, e.g.
 * ```
 * <div id="some-id"></div>
 * <script>
 *     MEDIASPACE.DsLayout.renderStandaloneComponent(
 *         'some-id',
 *         MEDIASPACE.AppName.ComponentReference,
 *         <?php echo $this->jsCodeEcho($this->props + ['context' => $configContext]); ?>
 *     );
 * </script>
 * ```
 */
export const renderStandaloneComponent = <T,>(
    id: string,
    Component: ComponentType<T>,
    props: T & { context: Config },
    scoped = false
) => {
    const root = createRoot(document.getElementById(id)!);
    root.render(
        <TopLevelComponentWrappers
            context={props.context}
            scoped={scoped}
            /*
             * Automatically create Emotion cache key based on the DOM element's ID for scoped components.
             * The element ID is unique, so the cache key will be unique as well.
             *
             * For non-scoped components, the prop will be ignored.
             */
            cssPrefix={scoped ? `kms-${id}` : ""}
        >
            <Component {...props} />
        </TopLevelComponentWrappers>
    );
};

/**
 * Return a render function for rendering standalone component in DS context.
 * This helper could be used in a React app to prepare a component render function for the client side.
 * The render function takes element ID as the first argument, and component's props with a context object as the second argument,
 * so it would be used like the following on the PHP side:
 * ```
 * <div id="some-id"></div>
 * <script>
 *     MEDIASPACE.AppName.render('some-id', <?php echo $this->jsCodeEcho($this->props + ['context' => $configContext]); ?>);
 * </script>
 * ```
 */
export const getStandaloneComponentRenderer =
    <T,>(component: ComponentType<T>, scoped = false) =>
    (id: string, props: T & { context: Config }) =>
        renderStandaloneComponent(id, component, props, scoped);
